import { CellType, ColumnSource, RowSource, RequestFilters, CellModel } from "@omegagrid/grid";
import { ColorTypes, dates } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { NodeSource, TreeAdapter, TreeOptions, TreeSource } from "@omegagrid/tree";
import rp, { GridEditorAdapter, GridEditorOptions, RowData, ServiceErrorBody, ServiceException, formatDateTime } from "@reporting/rp-app-host";
import { MDM_TABLE_COL_TYPES_MAP, MdmJsonColumnDefinition, MdmColTypes } from "../types";
import { TableDefinition, MdmColumnDefinition, MdmValues } from "@reporting/rp-types/mdm";
import { AccountingRights } from '@reporting/rp-types/core';
import { KEYED_TREE_OPTIONS } from "./keyedTree";

const PAGE_SIZE = 500;
const DATE_FORMAT = 'dd.MM.yyyy'; // must match the date format 104 used by the p_MDM_VALUES

// The service refused the batch because some of its rows were saved meanwhile by another user or from another window
const isStaleRowsError = (error: ServiceErrorBody) => error.status === 409 && error.error === 'row_changed';

class DropdownTreeAdapter extends TreeAdapter {

	private items: NodeSource[];

	constructor(
		private idMd: number,
		private options?: Partial<TreeOptions>
	) { super() }

	async getTree(): Promise<TreeSource> {
		if (this.items) return this.items;
		const values = await rp.ms('mdm').get<any[]>(`values/${this.idMd}`, {
			order: [['PROPERTY_1', 'asc']],
			fields: ['PROPERTY_1', 'PROPERTY_2'],
		});
		if (values.length && values[0].PROPERTY_2 !== undefined) {
			values.sort((a, b) => {
				return (a.PROPERTY_2 || '').toString().localeCompare((b.PROPERTY_2 || '').toString());
			});
		}
		this.items = values.map(val => ({
			k: val.PROPERTY_1?.toString() || null,
			v: val.PROPERTY_2 || val.PROPERTY_1 }));
		return this.items;
	}

	getOptions(): Partial<TreeOptions> {
		return { ...KEYED_TREE_OPTIONS, ...this.options };
	}

}

class ListTreeAdapter extends TreeAdapter {

	private tree: TreeSource;

	constructor(
		private idMd: number,
		private options?: Partial<TreeOptions>
	) { super() }

	async getTree(): Promise<TreeSource> {
		if (this.tree) return this.tree;
		const result = await rp.ms('etl').get<TreeSource[]>(`exec/${this.idMd}`, 
			{ scope: 1 }, 
			{ headers: { accept: 'application/json' } }
		);
		this.tree = result[0];
		for (const item of this.tree as (NodeSource & { selectable?: boolean|number|string })[]) {
			if (item.key) item.key = `${item.key}`; // ensure keys are strings
			// a list thread may flag group headings with selectable = 0: such a node gets no checkbox
			// and cannot be picked, only expanded (same as the legacy tree select)
			if (item.selectable === false || item.selectable === 0 || item.selectable === '0') item.ch = false;
			delete item.selectable;
		}
		return this.tree;
	}

	getOptions(): Partial<TreeOptions> {
		return { ...KEYED_TREE_OPTIONS, ...this.options };
	}

}

export class MdmValuesGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[] = [];
	private propertyNumbers: number[] = [];
	private table: TableDefinition;

	constructor (
		private idTable: number
	) { super() }

	async getColumns(): Promise<ColumnSource[]> {
		this.table = await rp.ms('mdm').get<TableDefinition>(`tableDefinitions/${this.idTable}`);
		const columnDefs = await rp.ms('mdm').get<MdmColumnDefinition[]>(`columnDefinitions/${this.table.id_md}`);
		columnDefs.sort((a, b) => {
			const ordA = a.property_ord ?? a.property;
			const ordB = b.property_ord ?? b.property;
			if (ordA < ordB) return -1;
			if (ordA > ordB) return 1;
			return 0;
		});
		this._columns = [{
			data: 'controls',
			t: 'a',
			e: false,
			w: 5 + 20 * 2,
			content: this.getRowControls(),
			filter: false,
			sorting: false,
			hidden: !this.hasWriteAccess()
		}, {
			data: 'LOCK_FLAG',
			header: 'LOCKED',
			t: 'b',
			w: 70,
			e: true,
			hidden: !this.table.lockable,
		}, {
			data: 'HIDDEN_FLAG',
			header: 'HIDDEN',
			t: 'b',
			w: 70,
			e: true,
			hidden: !this.table.show_hidden
		}];
		for (const cd of columnDefs.filter(c => c.property_name)) {
			this.propertyNumbers.push(cd.property);
			if (cd.property_type === MdmColTypes.JSON) {
				this._columns.push(...this.createJsonColumns(cd));
			} else {
				this._columns.push({
					data: `PROPERTY_${cd.property}`,
					header: cd.property_name,
					t: MDM_TABLE_COL_TYPES_MAP.find(c => c.colType === cd.property_type)?.t as CellType || 't',
					e: !cd.property_ro,
					w: 'auto',
					adapter: this.getAdapter(cd.property_type as MdmColTypes, cd.property_id_md),
					...(cd.property_pattern ? { z: cd.property_pattern } : {}),
				});
			}
		}
		this._columns.push({
			data: 'DATE_CREATED',
			header: 'DATE_CREATED',
			t: 'dt',
			e: false,
			w: 145,
			z: dates.getDateFormat({ withTime: true }),
			hidden: !this.table.show_date_created
		}, {
			data: 'WHO_CREATED',
			header: 'WHO_CREATED',
			t: 't',
			e: false,
			w: 180,
			hidden: !this.table.show_who_created
		}, {
			data: 'IDZ',
			t: 'n',
			e: false,
			hidden: true
		}, {
			data: '_hidden',
			t: 'b',
			e: false,
			hidden: true
		}, {
			data: '_locked',
			t: 'b',
			e: false,
			hidden: true
		});
		return this._columns;
	}

	private getAdapter(type: MdmColTypes, idMd: number): TreeAdapter | null {
		if (!idMd) return null;
		if (type === MdmColTypes.Treelist) return new ListTreeAdapter(idMd);
		if (type === MdmColTypes.Dropdown) return new DropdownTreeAdapter(idMd);
		return null;
	}

	private createJsonColumns(columnDef: MdmColumnDefinition): ColumnSource[] {
		const columns: ColumnSource[] = [];
		const jsonColumns = JSON.parse(columnDef.property_pattern) as MdmJsonColumnDefinition[];
		jsonColumns.forEach((jsonCol, i) => {
			columns.push({
				data: `PROPERTY_${columnDef.property}_${i}`,
				header: jsonCol.name || jsonCol.key,
				t: MDM_TABLE_COL_TYPES_MAP.find(c => c.colType === jsonCol.type)?.t as CellType,
				e: !jsonCol.ro,
				w: 'auto',
				filter: true,
				sorting: true,
				props: { virtual: true },
			});
		});
		return columns;
	}

	getRowControls = (locked: boolean = false) => {
		if (locked) return [];
		return [{
			action: 'delete',
			title: msg('Delete'),
			icon: 'trash',
			color: ColorTypes.Red
		}, {
			action: 'duplicate',
			title: msg('Duplicate'),
			icon: 'copy',
			color: ColorTypes.Yellow,
		}];
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const values = await rp.ms('mdm').get<any[]>(`values/${this.table.id_md}`, this.createRequestParams(filters, {
			count: PAGE_SIZE
		}));
		const rows: RowSource[] = [];
		for (const data of values) {
			const row: RowSource = { e: data.LOCK_FLAG || !this.hasWriteAccess() ? false : null, data: { ...data } }; // e: null = editable according to column definition
			for (const i of this.propertyNumbers) {
				row.data[`PROPERTY_${i}`] = data[`PROPERTY_${i}_DV`] ? { v: data[`PROPERTY_${i}`], dv: data[`PROPERTY_${i}_DV`] } : data[`PROPERTY_${i}`];
			}
			row.data.controls = { content: this.getRowControls(data.LOCK_FLAG) },
			row.data.LOCK_FLAG = { v: data.LOCK_FLAG, p: 1, e: this.hasLockAccess(data) },
			row.data.WHO_CREATED = { v: data.WHO_CREATED, dv: data.WHO_CREATED_DV },
			row.data._locked = data.LOCK_FLAG,
			row.data._hidden = data.HIDDEN_FLAG,
			rows.push(row);
		}
		return rows;
	}

	hasLockAccess(data?: any): boolean {
		if (!this.hasWriteAccess()) return false;
		if (!data.LOCK_FLAG) return true;
		return rp.userProvider.hasRight(AccountingRights.MDMDefine) || data.WHO_LOCKED === rp.userProvider.id;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			refreshAfterSave: false,
			filters: true,
			pageSize: PAGE_SIZE,
			numberFormat: '0',
			dateFormat: dates.getDateFormat(),
			toolbar: {
				save: { enabled: this.hasWriteAccess() },
				discard: { enabled: this.hasWriteAccess() },
				new: { enabled: this.hasWriteAccess() },
				rowPasteMode: { enabled: this.hasWriteAccess() },
			},
			showEmptyRow: true,
			rowMenu: {
				addRows: true,
				deleteRows: true,
			},
		});
	}

	hasWriteAccess(): boolean {
		return rp.userProvider.hasRight(AccountingRights.MDMDefine) || rp.userProvider.hasRight(AccountingRights.MDMWrite);
	}

	async save(rowData: RowData[]) {
		// extract date columns to apply correct format
		const dateColumns = this._columns.filter(col => col.t === 'd').map(col => col.data);
		// filter out rows that are marked for deletion (keyboard shortcut or menu), but locked
		const data = rowData
			.filter(row => !(row.state === 'deleted' && row.data._locked))
			.map(row => {
				const item: Record<string, any> = {
					_state: row.state,
					_index: row.index,
					id_md: this.table.id_md,
					...row.data,
					DATE_CREATED: formatDateTime(row.data.DATE_CREATED as Date, 'yyyy-MM-dd HH:mm:ss'), // timezone fix
				};
				for (const col of dateColumns) {
					if (item[col] && typeof item[col] === 'number')
						item[col] = formatDateTime(item[col] as number, DATE_FORMAT, true);
				}
				return item;
			});
		let saved: MdmValues[];
		try {
			saved = await rp.ms('mdm').post<MdmValues[]>(`values`, data, { type: this.table.type }, { showAlerts: e => !isStaleRowsError(e) });
		} catch (e) {
			// nothing was saved and the grid keeps the pending edits (GridEditor.save); the user refreshes and repeats the change
			if (e instanceof ServiceException && isStaleRowsError(e.data)) {
				const n = parseInt(e.data.message) || 1;
				rp.host.showAlert({
					type: 'danger',
					title: msg('MDM'),
					content: msg('Some of the rows you are saving ({n}) have been changed or deleted in the meantime by another user or from another window. Refresh the table and repeat the change.', { data: { n } }),
					actions: [{
						text: msg('Refresh'),
						icon: 'arrows-rotate',
						close: false, // the refresh first asks to discard the pending edits: declined, the alert stays
						exec: async alert => {
							await this.editor?.refresh(true);
							if (this.editor?.isSaved ?? true) alert.close();
						},
					}],
				});
			}
			throw e;
		}
		this.mergeInsertedRows(data, saved);
	}

	/**
	 * Merge the generated values for inserted rows returned by the server into the grid model.
	 */
	private mergeInsertedRows(sent: Record<string, any>[], saved: MdmValues[]): void {
		if (!Array.isArray(saved) || !saved.length) return;
		const grid = this.editor?.grid;
		if (!grid) return;

		const idzCol = grid.model.columns.find(c => c.data === 'IDZ')?.index;
		const dateCreatedCol = grid.model.columns.find(c => c.data === 'DATE_CREATED')?.index;
		const whoCreatedCol = grid.model.columns.find(c => c.data === 'WHO_CREATED')?.index;
		const whoCreatedDv = rp.userProvider.user?.login_name ?? rp.userProvider.id;

		let savedIndex = 0;
		for (const item of sent) {
			if (item._state === 'deleted') continue; // deleted rows are not returned by the server
			const values = saved[savedIndex++];
			if (!values) continue;

			const cells: CellModel[] = [];
			const setCell = (colIndex: number | undefined, value: unknown, dv?: string) => {
				if (colIndex == null) return;
				const cell = grid.model.getCellByCoords(item._index, colIndex);
				if (!cell) return;
				cell.value = value; // grid coerces per column type, same as the reload path
				if (dv !== undefined) cell.displayValue = dv; // display value must be set after value
				cells.push(cell);
			};

			setCell(idzCol, values.idz);
			setCell(dateCreatedCol, values.date_created);
			setCell(whoCreatedCol, values.who_created, whoCreatedDv);

			if (cells.length) grid.rendering.updateCells(cells);
		}
	}
}
